import { COLORS } from '../config';

const CURVE = [
  { n:10,  pct:20, label:"10 words",  desc:"Basic pronouns and the most common verbs" },
  { n:30,  pct:45, label:"30 words",  desc:"Add questions and core expressions" },
  { n:50,  pct:58, label:"50 words",  desc:"Family, food, and daily life words unlock most home conversations" },
  { n:81,  pct:70, label:"81 words",  desc:"Our full set covers roughly 70% of everyday spoken Tamil" },
  { n:200, pct:83, label:"200 words", desc:"Literary Tamil and nuanced vocabulary for reading texts" },
  { n:500, pct:92, label:"500 words", desc:"Advanced learner territory with most classical literature accessible" },
  { n:2000,pct:99, label:"2,000+",    desc:"The long tail of rare literary, technical, and archaic roots" },
];

export default function RationalePage() {
  return (
    <div style={{ minHeight:'100vh', background: COLORS.bg, color: COLORS.textPrimary }}>

      {/* Header — left-aligned, no gradient banner */}
      <div style={{ maxWidth:'680px', margin:'0 auto', padding:'clamp(40px,8vw,64px) 20px 0' }}>
        <div style={{ fontSize:'0.6875rem', fontWeight:600, color: COLORS.primary, letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:'8px' }}>The Methodology</div>
        <h1 style={{ margin:'0 0 12px', fontSize:'clamp(1.75rem,5vw,2.5rem)', fontWeight:800, letterSpacing:'-0.02em', lineHeight:1.1 }}>Why 81 Words?</h1>
        <p style={{ margin:'0 0 40px', maxWidth:'520px', fontSize:'1rem', color: COLORS.textSecondary, lineHeight:1.7 }}>
          Tamil has over 2,000 verb roots and tens of thousands of words. So why 81? Inspired by the 81 verses of the Thirukkolur Pen Pillai Ragasiyam, a revered text on devotion and surrender. Here is the Pareto logic behind tamizh.fun.
        </p>
      </div>

      <div style={{ maxWidth:'680px', margin:'0 auto', padding:'0 20px 64px' }}>

        {/* Dedication — no card, just content with left border accent */}
        <div style={{ paddingLeft:'20px', borderLeft:`3px solid ${COLORS.primary}`, marginBottom:'48px' }}>
          <div style={{ fontSize:'0.6875rem', fontWeight:600, color: COLORS.primary, letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:'8px' }}>Dedicated To</div>
          <div style={{ fontSize:'1.125rem', fontWeight:700, color: COLORS.textPrimary, marginBottom:'8px' }}>
            Shri U.Ve. Anbil Chakram Srinivasan Swami
          </div>
          <p style={{ fontSize:'0.875rem', lineHeight:1.8, color: COLORS.textSecondary, margin:0 }}>
            A beloved teacher and scholar who taught his grandchildren Tamil by breaking words into two-letter groups, then three, then four, long before any app existed. He showed that learning begins at home, one simple word at a time. tamizh.fun carries forward his method and his love for the Tamil language.
          </p>
        </div>

        {/* The inspiration — no card */}
        <div style={{ marginBottom:'48px' }}>
          <div style={{ fontSize:'0.6875rem', fontWeight:600, color: COLORS.primary, letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:'12px' }}>The Inspiration</div>
          <p style={{ fontSize:'0.875rem', lineHeight:1.8, color: COLORS.textSecondary, marginBottom:'12px' }}>
            The idea behind tamizh.fun started with a grandfather teaching Tamil to his grandchildren the traditional way: two-letter words first, then three, then four, building confidence with each step. That simple, loving method is the heart of this project.
          </p>
          <p style={{ fontSize:'0.875rem', lineHeight:1.8, color: COLORS.textSecondary, marginBottom:'12px' }}>
            The implementation at scale was inspired by researcher Rohan Pandey (@khoomeik), who used Claude to analyse 270+ Sanskrit texts and found that just 192 verb roots cover roughly 90% of all verbs in classical literature. The same Pareto principle applies to Tamil, and to every language. With AI now available, we can apply this frequency analysis to help thousands of families.
          </p>
          <p style={{ fontSize:'0.875rem', lineHeight:1.8, color: COLORS.textSecondary, margin:0 }}>
            tamizh.fun applies the same frequency-analysis methodology to Tamil, examining both spoken colloquial Tamil and literary Centamil to find the words that unlock the most of the language in the shortest time.
          </p>
        </div>

        {/* Frequency curve */}
        <div style={{ marginBottom:'48px' }}>
          <h2 style={{ fontSize:'1.25rem', fontWeight:700, color: COLORS.textPrimary, marginBottom:'8px' }}>The Frequency Curve</h2>
          <p style={{ fontSize:'0.875rem', lineHeight:1.8, color: COLORS.textSecondary, marginBottom:'20px' }}>
            Not all words are equal. In any language, a small set of words appears overwhelmingly more than the rest. This is Zipf's Law.
          </p>

          {/* Tamil facts — inline, not cards */}
          <div style={{ display:'flex', gap:'24px', flexWrap:'wrap', marginBottom:'24px', fontSize:'0.8125rem', color: COLORS.textMuted }}>
            <span><strong style={{ color: COLORS.primary, fontWeight:700 }}>2k-4k</strong> words in daily speech</span>
            <span><strong style={{ color: COLORS.primary, fontWeight:700 }}>10k-20k</strong> fluent speaker vocab</span>
            <span><strong style={{ color: COLORS.primary, fontWeight:700 }}>124,405</strong> in Tamil Lexicon</span>
            <span><strong style={{ color: COLORS.primary, fontWeight:700 }}>1.5M+</strong> digital entries</span>
          </div>

          {/* Bar chart */}
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {CURVE.map((c, i) => {
              const isTarget = c.n === 81;
              return (
                <div key={i} style={{ display:'flex', gap:'12px', alignItems:'center' }}>
                  <div style={{ minWidth:'72px', fontSize:'0.75rem', color: COLORS.textMuted, textAlign:'right', fontWeight: isTarget ? 700 : 400 }}>{c.label}</div>
                  <div style={{ flex:1, height: isTarget ? '28px' : '20px', borderRadius:'6px', background: COLORS.bgAlt, overflow:'hidden', position:'relative' }}>
                    <div style={{
                      height:'100%',
                      width:`${c.pct}%`,
                      background: isTarget ? COLORS.primary : COLORS.border,
                      borderRadius:'6px',
                      transition:'width 0.5s cubic-bezier(0.16,1,0.3,1)',
                      display:'flex', alignItems:'center', justifyContent:'flex-end', paddingRight:'8px',
                    }}>
                      <span style={{ fontSize:'0.625rem', fontWeight:700, color: isTarget ? '#fff' : COLORS.textMuted }}>{c.pct}%</span>
                    </div>
                  </div>
                  <div style={{ minWidth:'0', flex:'0 1 220px', fontSize:'0.75rem', color: isTarget ? COLORS.primary : COLORS.textMuted, lineHeight:1.4, fontWeight: isTarget ? 700 : 400 }}>{c.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Spoken vs Literary — side by side, no card borders */}
        <div style={{ marginBottom:'48px' }}>
          <h2 style={{ fontSize:'1.25rem', fontWeight:700, color: COLORS.textPrimary, marginBottom:'8px' }}>Why the Spoken / Literary Split?</h2>
          <p style={{ fontSize:'0.875rem', lineHeight:1.8, color: COLORS.textSecondary, marginBottom:'16px' }}>
            Tamil is unique among Indian languages in maintaining a strong diglossia: a deep split between the formal written language (Centamil) and the spoken colloquial dialects.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px' }}>
            <div>
              <div style={{ fontSize:'0.875rem', fontWeight:700, color: COLORS.tamilGold, marginBottom:'6px' }}>Spoken Tamil</div>
              <div style={{ fontSize:'0.8125rem', color: COLORS.textSecondary, lineHeight:1.7 }}>What kids hear at home, in Tamil films, and in WhatsApp messages. Prioritise this first for diaspora children to start communicating quickly.</div>
            </div>
            <div>
              <div style={{ fontSize:'0.875rem', fontWeight:700, color: COLORS.primary, marginBottom:'6px' }}>Literary Tamil</div>
              <div style={{ fontSize:'0.8125rem', color: COLORS.textSecondary, lineHeight:1.7 }}>The formal classical tradition: Thirukkural, Sangam poetry, temple inscriptions. Essential for reading, heritage, and cultural pride.</div>
            </div>
          </div>
        </div>

        {/* Methodology steps — numbered, no cards */}
        <div style={{ marginBottom:'48px' }}>
          <h2 style={{ fontSize:'1.25rem', fontWeight:700, color: COLORS.textPrimary, marginBottom:'16px' }}>How We Built the List</h2>
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            {[
              'Started from Srinivasan\'s handwritten baseline list of essential Tamil words he used to teach his grandchildren',
              'Collected 270+ Tamil texts spanning classical literature, folk songs, contemporary stories, and everyday speech',
              'Used Claude to identify and normalise root forms across morphological variations (Tamil verbs conjugate heavily)',
              'Ranked by frequency across the full corpus, cross-checked against learner difficulty and communicative value',
              'Manually reviewed by Tamil speakers to ensure spoken/literary tags are accurate and tips are culturally appropriate',
            ].map((desc, i) => (
              <div key={i} style={{ display:'flex', gap:'16px', alignItems:'flex-start' }}>
                <div style={{ width:'28px', height:'28px', borderRadius:'50%', background: COLORS.primaryLight, color: COLORS.primary, fontWeight:700, fontSize:'0.8125rem', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i+1}</div>
                <div style={{ fontSize:'0.875rem', color: COLORS.textSecondary, lineHeight:1.7, paddingTop:'4px' }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* More languages — subtle CTA */}
        <div style={{ marginBottom:'48px', paddingTop:'32px', borderTop:`1px solid ${COLORS.border}` }}>
          <div style={{ fontSize:'1.125rem', fontWeight:700, color: COLORS.textPrimary, marginBottom:'8px' }}>More Languages Coming</div>
          <p style={{ fontSize:'0.875rem', color: COLORS.textSecondary, lineHeight:1.7, marginBottom:'16px' }}>
            We are testing the same frequency methodology for <strong style={{color: COLORS.textPrimary}}>Telugu</strong> and <strong style={{color: COLORS.textPrimary}}>Kannada</strong> next. Tamil is the pilot. Your support keeps it going.
          </p>
          <a href="/donate" style={{
            display:'inline-flex', alignItems:'center', justifyContent:'center',
            padding:'12px 24px', borderRadius:'10px',
            background: COLORS.primary, color:'#fff', fontWeight:700, fontSize:'0.875rem', textDecoration:'none', minHeight:'48px',
          }}>
            Support tamizh.fun
          </a>
        </div>

        {/* Footer */}
        <div style={{ paddingTop:'24px', borderTop:`1px solid ${COLORS.border}`, fontSize:'0.75rem', color: COLORS.textMuted }}>
          Supported by <a href="https://virat.org.uk" target="_blank" rel="noopener noreferrer" style={{color: COLORS.primary, textDecoration:'none', fontWeight:600}}>VIRAT UK</a> · Built with love for the Tamil diaspora
        </div>
      </div>
    </div>
  );
}
